import { useWebClientApi } from "src/api";
import {
  fromBiologicalSexes,
  toBiologicalSexesUpdateReq,
  type SexFormData,
} from "src/api/adapters";
import { useResponseRedirect, useSectionProgress } from "src/hooks";
import { useDlaFormSubmit } from "src/hooks/useDlaFormSubmit";
import { useFormLoad } from "src/hooks/useFormLoad";
import FormLayout from "src/layouts/FormLayout";
import { Col } from "src/components/layout";
import Disclaimer from "./EthnicityDisclaimer";
import type { DtoGetDemographicsResp } from "@porchpass/api-client";
import { test, validate, hasBiologicalSex } from "src/utils";
import {
  EcoaSexFields,
  FormTable,
  FormTableBody,
  UserIdVerificationIcon,
} from "@porchpass/ui";
import { useMemo } from "react";
import {
  fromEcoaSexFormData,
  toEcoaSexFormData,
} from "src/forms/adapters/ecoa";
import { fieldErrorsAfterSubmit } from "src/forms/fieldErrorsAfterSubmit";

const validationMapper = () => ({
  biological_sexes: [test(hasBiologicalSex, "Must select at least one option")],
});

export default () => {
  const api = useWebClientApi();
  const sectionProgress = useSectionProgress();
  const { formData, updateField } = useFormLoad<SexFormData>(
    {},
    () => api.getDemographics(),
    (data) => fromBiologicalSexes(data as DtoGetDemographicsResp),
  );
  const [submitHandler, loading, response, hasAttemptedFormSubmission] =
    useDlaFormSubmit((data: SexFormData) =>
      api.updateDemographics(toBiologicalSexesUpdateReq(data)),
    );
  const nextPagePath =
    sectionProgress["demographics"] && sectionProgress["loan_overview"]
      ? "../../progress"
      : "/final-steps/consent";
  useResponseRedirect(nextPagePath, response);

  const formErrors = validate(formData as never, validationMapper as never) as {
    biological_sexes?: { message: string };
  };

  const sexValue = useMemo(
    () =>
      toEcoaSexFormData(formData as Parameters<typeof toEcoaSexFormData>[0]),
    [formData],
  );

  const visibleErrors = fieldErrorsAfterSubmit(
    { biologicalSex: formErrors.biological_sexes?.message },
    hasAttemptedFormSubmission,
  );

  return (
    <FormLayout
      title="What is your sex?"
      headerTitle="Ethnic Background"
      stepIndex={9}
      footerTitle={
        sectionProgress["demographics"] && sectionProgress["loan_overview"]
          ? "Save"
          : "Next"
      }
      clickHandler={() => void submitHandler(formData, formErrors as never)}
      isLoading={loading}
    >
      <Col>
        <FormTable
          title="Equal Credit Opportunity Act"
          headerIcon={
            <UserIdVerificationIcon
              variant="bulk-rounded"
              size={16}
              className="text-[#CA7900]"
            />
          }
        >
          <FormTableBody>
            <EcoaSexFields
              value={sexValue}
              onChange={(patch) => {
                const payload = fromEcoaSexFormData({
                  ...sexValue,
                  ...patch,
                });
                updateField("biological_sexes")(payload.biological_sexes ?? []);
              }}
              errors={visibleErrors}
            />
          </FormTableBody>
        </FormTable>
        <Disclaimer />
      </Col>
    </FormLayout>
  );
};
